import React, { createContext, type ReactNode, useContext, useEffect } from 'react'
import preferenceKeys from '../person-page/preference-keys'
import type { ProviderValue } from './PreferenceProvider'
import PreferenceProvider from './PreferenceProvider'
import { localStorageSetItem } from '../utils/localStorage'

export type SectionOrderOptions = string[]
export const defaultSectionOrder: SectionOrderOptions = [
  'vitals',
  'other-information',
  'family-members',
  'other-relationships',
  'lifesketch',
]

type SectionOrderValue = ProviderValue<SectionOrderOptions>

const SectionOrderContext = createContext<SectionOrderValue>([defaultSectionOrder, () => undefined])

function SectionOrderLocalStorageSync(): null {
  const [sectionOrder] = useContext(SectionOrderContext)

  // Seed localStorage whenever the loaded preference value changes so the skeleton
  // can read it synchronously on the next page load before the async fetch completes.
  useEffect(() => {
    localStorageSetItem(preferenceKeys.SECTION_ORDER, JSON.stringify(sectionOrder))
  }, [sectionOrder])

  return null
}

export function useSectionOrder(): SectionOrderValue {
  const [sectionOrder, setSectionOrder] = useContext(SectionOrderContext)

  const setWithLocalStorage = (newOrder: SectionOrderOptions): void => {
    localStorageSetItem(preferenceKeys.SECTION_ORDER, JSON.stringify(newOrder))
    setSectionOrder(newOrder)
  }

  return [sectionOrder, setWithLocalStorage]
}

type Props = {
  children: ReactNode
  loadingComponent: ReactNode
}

export default function SectionOrderProvider({ children, loadingComponent }: Props): JSX.Element {
  return (
    <PreferenceProvider
      preferenceKey={preferenceKeys.SECTION_ORDER}
      Context={SectionOrderContext}
      loadingComponent={loadingComponent}
      defaultValue={defaultSectionOrder}
    >
      <SectionOrderLocalStorageSync />
      {children}
    </PreferenceProvider>
  )
}
